/**
 * The fan chart (Design Review V-2; PRD 10.4; Phase 40): the sampled pipeline mode drawn, so a
 * planner can read how *likely* an overload is and not only how large the average one would be.
 *
 * Phase 39 drew the coin five hundred times and stored what each week looked like in each draw
 * (`forecast_samples`, `GET /forecast/samples`). This module is the vocabulary that turns those
 * rows into a picture and back into a table, shared by the card, the CSV export and the tests so
 * none of them can hold a different opinion of what the band is.
 *
 * Four rules live here:
 *
 * - **The value axis is utilization, not hours.** The question the fan answers is "how many of
 *   the futures are over the line", and the line is the overload threshold — 115 per cent of a
 *   week's capacity. Capacity moves from week to week (holidays, leavers, PTO), so in hours the
 *   threshold would be a staircase and a band that rose while capacity rose faster would look
 *   worse rather than better. Divided through by the week's own frozen capacity it is one flat
 *   reference at 1.15, which is what V-2 asks for and what the percentage labels are read
 *   against. Hours are not lost: they are what the tooltip and the table carry, and the CSV
 *   exports.
 * - **The two bands wear the confidence fills, and they mean what the fills mean.** The outer
 *   band holds eight draws in ten and the inner one holds five, so the outer is drawn in the
 *   pipeline layer's dot field (PRD 7.3's low-confidence fill) and the inner in the hatch
 *   (medium). The hue is the ribbon's pipeline layer, `--type-4`, because the uncertainty being
 *   drawn is that layer's and no other: everything else in a week is committed and enters every
 *   draw unchanged.
 * - **Nothing here is aggregated.** A percentile is not a sum, so a fan is always one row of one
 *   sampled dimension — the company, or one service — never several added together. The card
 *   picks the row; this module never merges two.
 * - **A week with no capacity has no utilization and therefore no band.** It is a gap in the
 *   fan, drawn as a gap, never as a zero: the line breaks the way the small multiples' lines
 *   break, and the table still carries the hours.
 *
 * Pure: no DOM, no I/O.
 */
import { col } from './table.js';

/** The shared filters this view reads, in the API's names. `pipeline` is not among them: the
 *  samples are a distribution over both modes' rows, so there is nothing for it to select. */
export const FAN_PARAM_KEYS = Object.freeze(['run_id', 'from', 'weeks', 'dimension', 'entity']);

/** The dimensions a deal's hours can be sampled in — `SAMPLE_DIMENSIONS` of Phase 39, restated
 *  here so the browser bundle does not reach into a server module. A test pins the two equal. */
export const FAN_DIMENSIONS = Object.freeze(['company', 'service']);

/** The hue the fan wears: the ribbon's pipeline layer, the one layer a draw can move. */
export const FAN_TOKEN = '--type-4';

/** The percentiles the band is built from, in order. */
export const FAN_PERCENTILES = Object.freeze(['p10', 'p25', 'p50', 'p75', 'p90']);

/**
 * The two bands, widest first so the narrower one is drawn over it. `share` is the share of
 * draws each contains, which is what the legend says out loud; `fill` is the confidence fill
 * that share earns under PRD 7.3.
 */
export const FAN_BANDS = Object.freeze([
  Object.freeze({
    key: 'p10_p90',
    lo: 'p10',
    hi: 'p90',
    label: 'P10 to P90',
    share: 0.8,
    token: FAN_TOKEN,
    fill: 'dots',
  }),
  Object.freeze({
    key: 'p25_p75',
    lo: 'p25',
    hi: 'p75',
    label: 'P25 to P75',
    share: 0.5,
    token: FAN_TOKEN,
    fill: 'hatch',
  }),
]);

/**
 * The lines drawn over the bands. The median is the fan's own answer; the other two are the
 * point estimates the other three views draw, put on the same axis so the gate's "the band sits
 * between the weighted and the unweighted totals" is a thing a reader can see rather than a
 * thing they have to take on trust.
 */
export const FAN_LINES = Object.freeze([
  Object.freeze({
    key: 'p50',
    label: 'Median (P50)',
    stroke: FAN_TOKEN,
    width: 2.5,
    dash: null,
  }),
  Object.freeze({
    key: 'weighted',
    label: 'Weighted total',
    stroke: '--ink-strong',
    width: 1.5,
    dash: '5 3',
  }),
  Object.freeze({
    key: 'unweighted',
    label: 'Unweighted total',
    stroke: '--ink-muted',
    width: 1.25,
    dash: '2 3',
  }),
]);

/** The keys a point carries a value for, percentiles and the three bracketing figures. */
export const FAN_VALUE_KEYS = Object.freeze([
  ...FAN_PERCENTILES,
  'mean',
  'committed',
  'weighted',
  'unweighted',
]);

/** A week is labelled with its probability once it reaches this; below it the label is noise. */
export const FAN_LABEL_MIN = 0.05;

/** At most this many probability labels, so a 26-week window does not become a wall of text. */
export const FAN_LABELS_MAX = 8;

/** More draws over the threshold than under it: the week the card counts as at risk. */
export const FAN_OVER_LIKELY = 0.5;

/** The axis is given this much room above the tallest thing on it. */
export const SCALE_HEADROOM = 1.06;

export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const round4 = (n) => Math.round((n + Number.EPSILON) * 10000) / 10000;
const num = (v) => (v === null || v === undefined || v === '' ? 0 : Number(v) || 0);

/** Utilization of hours against a capacity denominator; null with no capacity, as the engine. */
export function utilizationOf(hours, capacityHours) {
  const capacity = num(capacityHours);
  return capacity > 0 ? round4(num(hours) / capacity) : null;
}

/** The field a value key reads on a cell: `p50` + `utilization` → `p50_utilization`. */
export function valueKey(key, basis = 'utilization') {
  return `${key}_${basis}`;
}

/**
 * The order a sampled cell must come out in, and whether it did.
 *
 * `committed <= p10 <= p25 <= p50 <= p75 <= p90 <= unweighted` holds by construction — a draw
 * can only add whole deals to the committed hours, and the largest possible draw is all of them
 * — so a cell that breaks it is a bug, not a surprise, and the card says so on the row rather
 * than drawing a band nobody can trust. This is the review gate's arithmetic, in code.
 *
 * @param {object} cell  one `GET /forecast/samples` cell
 */
export function bandOrdering(cell) {
  const order = ['committed', ...FAN_PERCENTILES, 'unweighted'];
  const violations = [];
  for (let i = 1; i < order.length; i += 1) {
    const lower = num(cell?.[valueKey(order[i - 1], 'hours')]);
    const upper = num(cell?.[valueKey(order[i], 'hours')]);
    if (round2(upper) < round2(lower))
      violations.push({ above: order[i], below: order[i - 1], hours: round2(upper - lower) });
  }
  return { ok: violations.length === 0, violations };
}

/**
 * One cell of the response with the figures the fan needs and the API does not send: the
 * utilization of the three bracketing figures (it sends the percentiles' own), how wide the
 * outer band is, and the ordering check.
 *
 * @param {object} cell
 * @param {{ threshold?: number }} [opts]
 */
export function fanCell(cell, { threshold = null } = {}) {
  const capacity = num(cell?.capacity_hours);
  const u = (hours) => utilizationOf(hours, capacity);
  const ordering = bandOrdering(cell);
  const limit = threshold ?? cell?.threshold ?? null;
  return {
    ...cell,
    committed_utilization: u(cell?.committed_hours),
    weighted_utilization: u(cell?.weighted_hours),
    unweighted_utilization: u(cell?.unweighted_hours),
    mean_utilization: u(cell?.mean_hours),
    band_hours: round2(num(cell?.p90_hours) - num(cell?.p10_hours)),
    band_utilization:
      capacity > 0 ? round4((num(cell?.p90_hours) - num(cell?.p10_hours)) / capacity) : null,
    threshold_hours: limit === null ? null : round2(capacity * limit),
    ordering_ok: ordering.ok,
    ordering_violations: ordering.violations,
    // The band reaches over the line in at least one draw. `prob_over_threshold` says how many.
    crosses: limit !== null && capacity > 0 && num(cell?.p90_hours) / capacity > limit,
    likely_over: num(cell?.prob_over_threshold) >= FAN_OVER_LIKELY,
  };
}

/** Every row of a response keyed by its entity key, for the row picker. */
export function fanRows(data) {
  return (data?.rows ?? []).map((r) => ({
    key: r.key,
    label: r.label ?? r.key,
    entity: r.entity ?? null,
    peak: r.peak ?? null,
    most_at_risk: r.most_at_risk ?? null,
  }));
}

/**
 * The row a fan opens on: the one whose worst week is likeliest to come out overloaded, ties to
 * the API's own order (which is label order). The company dimension has one row and the choice
 * does not arise; on the service dimension a reader opening the view is looking for the role
 * that might break, not for the alphabetically first one.
 *
 * @param {Array<object>} rows  `rows` of a `GET /forecast/samples` response
 */
export function defaultRowKey(rows) {
  let best = null;
  for (const r of rows ?? []) {
    const risk = num(r.most_at_risk?.prob_over_threshold);
    if (best === null || risk > best.risk) best = { key: r.key, risk };
  }
  return best?.key ?? null;
}

/** The named row of a response, or the default one. */
export function fanRow(data, key = null) {
  const rows = data?.rows ?? [];
  if (!rows.length) return null;
  const wanted = key ?? defaultRowKey(rows);
  return rows.find((r) => r.key === wanted) ?? rows[0];
}

/**
 * The model the card draws: one row's cells enriched, the threshold, and the row picker's rows.
 *
 * @param {object} data  a `GET /forecast/samples` response
 * @param {{ key?: string | null }} [opts]
 */
export function fanModel(data, { key = null } = {}) {
  const row = fanRow(data, key);
  const threshold = data?.threshold ?? data?.thresholds?.overloaded_above ?? null;
  return {
    row,
    key: row?.key ?? null,
    label: row?.label ?? null,
    threshold,
    rows: fanRows(data),
    cells: (row?.cells ?? []).map((c) => fanCell(c, { threshold })),
    draws: Number(data?.draws ?? 0),
    seed: Number(data?.seed ?? 0),
    dimension: data?.dimension ?? 'company',
  };
}

/**
 * The top of the value axis: the highest thing that has to fit — the top of the outer band, the
 * unweighted line, the threshold and the capacity line itself — with a little headroom, so the
 * threshold is always on screen even in a window where nothing comes near it.
 *
 * @param {Array<object>} cells  enriched cells
 * @param {{ threshold?: number | null, basis?: 'utilization' | 'hours' }} [opts]
 */
export function fanScaleMax(cells, { threshold = null, basis = 'utilization' } = {}) {
  let max = 0;
  for (const c of cells ?? []) {
    max = Math.max(max, num(c[valueKey('p90', basis)]), num(c[valueKey('unweighted', basis)]));
    if (basis === 'hours') max = Math.max(max, num(c.capacity_hours), num(c.threshold_hours));
  }
  if (basis === 'utilization') max = Math.max(max, 1, num(threshold));
  return round2(max * SCALE_HEADROOM);
}

/**
 * Where each week sits across the plot.
 *
 * Points are at week centres, with the first and last pulled out to the edges of their bands so
 * the fan spans the plot rather than floating inside it — the ribbon's rule, because the two are
 * drawn on the same axis and a reader flipping between them should see the same weeks in the
 * same places. A week with no capacity has no value and breaks the run.
 *
 * @param {Array<object>} cells  enriched cells
 * @param {{ x: Function, y: Function, basis?: string }} scales  `x` a band scale, `y` linear
 */
export function fanPoints(cells, { x, y, basis = 'utilization' }) {
  const list = cells ?? [];
  return list.map((c, i) => {
    const x0 = x(c.week_start);
    const x1 = x0 + x.bandwidth();
    const at = i === 0 ? x0 : i === list.length - 1 ? x1 : x.centre(c.week_start);
    const values = {};
    let missing = false;
    for (const key of FAN_VALUE_KEYS) {
      const v = c[valueKey(key, basis)];
      if (v === null || v === undefined || !Number.isFinite(Number(v))) missing = true;
      values[key] = v === null || v === undefined ? null : y(Number(v));
    }
    return { cell: c, index: i, week_start: c.week_start, x: at, x0, x1, values, missing };
  });
}

/** Runs of consecutive weeks that can be drawn: a gap splits the fan rather than bridging it. */
function runsOf(points) {
  const runs = [];
  let run = [];
  for (const p of points) {
    if (p.missing) {
      if (run.length) runs.push(run);
      run = [];
      continue;
    }
    run.push(p);
  }
  if (run.length) runs.push(run);
  return runs;
}

/**
 * The band between two value keys, as one path per run of drawable weeks: along the top, back
 * along the bottom. A run of one week is drawn as the width of its band, so a single week still
 * shows a band and not an invisible zero-width line.
 */
export function bandPath(points, loKey, hiKey) {
  const parts = [];
  for (const run of runsOf(points)) {
    if (run.length === 1) {
      const [p] = run;
      parts.push(
        `M${p.x0},${p.values[hiKey]} L${p.x1},${p.values[hiKey]} L${p.x1},${p.values[loKey]} L${p.x0},${p.values[loKey]} Z`,
      );
      continue;
    }
    const top = run.map((p) => `${p.x},${p.values[hiKey]}`);
    const base = run.map((p) => `${p.x},${p.values[loKey]}`).reverse();
    parts.push(`M${top.join(' L')} L${base.join(' L')} Z`);
  }
  return parts.join(' ');
}

/** One line across the drawable weeks, broken wherever a week has no capacity. */
export function linePath(points, key) {
  const parts = [];
  for (const run of runsOf(points)) {
    if (run.length === 1) {
      const [p] = run;
      parts.push(`M${p.x0},${p.values[key]} L${p.x1},${p.values[key]}`);
      continue;
    }
    parts.push(`M${run.map((p) => `${p.x},${p.values[key]}`).join(' L')}`);
  }
  return parts.join(' ');
}

/**
 * The weeks that carry a probability label.
 *
 * Every week has a probability and most of them are zero, so labelling all of them would bury
 * the ones that matter. The rule: a week is a candidate once its probability reaches `min`, and
 * when more weeks qualify than there is room for, the likeliest survive — but they are returned
 * in week order, because a label is read along the axis and not in a ranking.
 *
 * @param {Array<object>} cells  enriched cells
 * @param {{ min?: number, max?: number }} [opts]
 */
export function fanLabels(cells, { min = FAN_LABEL_MIN, max = FAN_LABELS_MAX } = {}) {
  const candidates = (cells ?? [])
    .map((c, index) => ({ index, cell: c, prob: c.prob_over_threshold }))
    .filter((c) => c.prob !== null && c.prob !== undefined && c.prob >= min);
  const kept =
    candidates.length <= max
      ? candidates
      : [...candidates]
          .sort((a, b) => b.prob - a.prob || a.index - b.index)
          .slice(0, max)
          .sort((a, b) => a.index - b.index);
  return kept.map((c) => ({
    index: c.index,
    week_start: c.cell.week_start,
    prob_over_threshold: c.prob,
    likely_over: c.cell.likely_over,
  }));
}

/** The cell with the largest value of `measure`, ties to the earlier week (the samples' rule). */
function peakBy(cells, measure) {
  let best = null;
  for (const c of cells ?? []) {
    const v = measure(c);
    if (v === null || v === undefined) continue;
    if (!best || v > measure(best)) best = c;
  }
  return best;
}

/**
 * The figures the card states above the chart, computed from the cells it drew rather than read
 * off the response's own `peak` — so a KPI that disagreed with the picture would be a test
 * failure and not a shrug. (A test pins the two equal.)
 *
 * @param {ReturnType<typeof fanModel>} model
 */
export function fanSummary(model) {
  const cells = model?.cells ?? [];
  const peak = peakBy(cells, (c) => c.p50_hours);
  const risk = peakBy(cells, (c) => c.prob_over_threshold);
  const widest = peakBy(cells, (c) => c.band_hours);
  return {
    weeks: cells.length,
    draws: model?.draws ?? 0,
    seed: model?.seed ?? 0,
    threshold: model?.threshold ?? null,
    deals: Math.max(0, ...cells.map((c) => Number(c.deals ?? 0)), 0),
    deals_unpriced: Math.max(0, ...cells.map((c) => Number(c.deals_unpriced ?? 0)), 0),
    peak: peak ?? null,
    most_at_risk: risk && num(risk.prob_over_threshold) > 0 ? risk : null,
    widest: widest && widest.band_hours > 0 ? widest : null,
    weeks_at_risk: cells.filter((c) => num(c.prob_over_threshold) > 0).length,
    weeks_likely_over: cells.filter((c) => c.likely_over).length,
    weeks_no_capacity: cells.filter((c) => num(c.capacity_hours) <= 0).length,
    ordering_ok: cells.every((c) => c.ordering_ok),
  };
}

/**
 * What a week's probability says in words — the Design Review's "40 per cent chance of exceeding
 * 115% in week 46", assembled from the cell so the sentence cannot drift from the label.
 *
 * @param {object} cell  an enriched cell
 * @param {{ percent?: Function, week?: Function }} [format]
 */
export function probabilityText(
  cell,
  { percent = (v) => `${Math.round((v ?? 0) * 100)}%`, week = (iso) => iso } = {},
) {
  if (!cell) return '';
  if (cell.prob_over_threshold === null || cell.prob_over_threshold === undefined)
    return `Week of ${week(cell.week_start)}: no capacity, so no threshold to cross`;
  return `${percent(cell.prob_over_threshold)} of ${cell.draws} draws put the week of ${week(
    cell.week_start,
  )} over ${percent(cell.threshold)} of capacity`;
}

/** The table behind the chart (PRD 7.3): one row per week, hours and the band in both units. */
export function fanColumns() {
  return [
    col.week('week_start', 'Week'),
    col.count('deals', 'Deals'),
    col.hours('committed_hours', 'Committed'),
    col.hours('p10_hours', 'P10'),
    col.hours('p25_hours', 'P25'),
    col.hours('p50_hours', 'Median (P50)'),
    col.hours('p75_hours', 'P75'),
    col.hours('p90_hours', 'P90'),
    col.hours('mean_hours', 'Mean'),
    col.hours('weighted_hours', 'Weighted'),
    col.hours('unweighted_hours', 'Unweighted'),
    col.hours('capacity_hours', 'Capacity'),
    col.percent('p50_utilization', 'Median utilization'),
    col.percent('prob_over_threshold', 'Chance over threshold'),
    col.flag('ordering_ok', 'Band ordered'),
  ];
}

/**
 * One row per week for the table and the CSV. Every figure is the endpoint's, except the
 * ordering flag, which is the check above.
 * @param {ReturnType<typeof fanModel>} model
 */
export function fanExportRows(model) {
  return (model?.cells ?? []).map((c) => ({
    week_start: c.week_start,
    deals: Number(c.deals ?? 0),
    committed_hours: round2(num(c.committed_hours)),
    p10_hours: round2(num(c.p10_hours)),
    p25_hours: round2(num(c.p25_hours)),
    p50_hours: round2(num(c.p50_hours)),
    p75_hours: round2(num(c.p75_hours)),
    p90_hours: round2(num(c.p90_hours)),
    mean_hours: round2(num(c.mean_hours)),
    weighted_hours: round2(num(c.weighted_hours)),
    unweighted_hours: round2(num(c.unweighted_hours)),
    capacity_hours: round2(num(c.capacity_hours)),
    p50_utilization: c.p50_utilization ?? null,
    prob_over_threshold: c.prob_over_threshold ?? null,
    ordering_ok: c.ordering_ok,
  }));
}

/**
 * The CSV's columns: the table's, with the row's identity in front of them.
 *
 * The screen draws one sampled row at a time because a percentile is not a sum and two rows
 * cannot be added together; a file has no such constraint and dropping the rows a reader did
 * not happen to have open would be a quiet loss, so the export carries every row of the
 * response and names each.
 */
export function fanRowsColumns() {
  return [col.text('label', 'Row'), ...fanColumns()];
}

/** Every sampled row of a response, flattened week by week for the CSV. */
export function fanRowsExportRows(data) {
  return (data?.rows ?? []).flatMap((r) =>
    fanExportRows(fanModel(data, { key: r.key })).map((row) => ({
      label: r.label ?? r.key,
      ...row,
    })),
  );
}
